// src/features/project-intake/components/FileUploadField.tsx

import React from 'react';

interface FileUploadFieldProps {
  label: string;
  file: File | null;
  onFileChange: (file: File | null) => void;
  onPreviewClick: (url: string) => void;
  required?: boolean;
  error?: string | null;
}

// File upload input with preview
export const FileUploadField = React.memo<FileUploadFieldProps>(
  ({ label, file, onFileChange, onPreviewClick, required, error }) => {
    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFile = e.target.files?.[0];
      if (selectedFile) {
        onFileChange(selectedFile);
      }
    };

    const handleRemoveFile = () => {
      onFileChange(null);
    };

    const renderPreview = () => {
      if (!file) return null;

      const fileUrl = URL.createObjectURL(file);
      const fileType = file.type;

      if (fileType.startsWith('image/')) {
        return (
          <img
            src={fileUrl}
            alt={file.name}
            className="max-w-full h-auto max-h-64 rounded cursor-pointer hover:opacity-90 transition-opacity"
            onClick={() => onPreviewClick(fileUrl)}
          />
        );
      } else if (fileType === 'application/pdf') {
        return (
          <div>
            <iframe
              src={fileUrl}
              className="w-full h-64 rounded border theme-border"
              title={file.name}
            />
            <button
              type="button"
              onClick={() => onPreviewClick(fileUrl)}
              className="mt-2 px-2 py-1 text-xs rounded border theme-border theme-card theme-text-secondary hover:bg-opacity-80"
            >
              Open in Lightbox
            </button>
          </div>
        );
      } else if (fileType.startsWith('video/')) {
        return (
          <div>
            <video src={fileUrl} controls className="max-w-full h-auto max-h-64 rounded" />
            <button
              type="button"
              onClick={() => onPreviewClick(fileUrl)}
              className="mt-2 px-2 py-1 text-xs rounded border theme-border theme-card theme-text-secondary hover:bg-opacity-80"
            >
              Open in Lightbox
            </button>
          </div>
        );
      } else {
        return (
          <div className="flex items-center gap-2 p-3 theme-bg-secondary rounded border theme-border">
            <span className="text-2xl">📄</span>
            <div>
              <div className="font-medium theme-text-primary">{file.name}</div>
              <div className="text-sm theme-text-tertiary">{(file.size / 1024).toFixed(2)} KB</div>
            </div>
          </div>
        );
      }
    };

    return (
      <div>
        <label className="text-sm font-semibold theme-text-primary">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <input
          type="file"
          onChange={handleFileUpload}
          className={`w-full border rounded px-2 py-1 text-sm theme-text-secondary file:mr-4 file:py-1 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold file:bg-blue-600/20 file:text-blue-500 hover:file:bg-blue-600/30 ${
            error ? 'border-red-500 ring-2 ring-red-900/20' : 'theme-border'
          }`}
        />
        {error && (
          <p className="text-red-400 text-xs mt-1 flex items-center gap-1">
            <span>⚠</span> {error}
          </p>
        )}
        {file && (
          <div className="mt-2 border theme-border rounded p-2 theme-bg-secondary">
            <div className="flex justify-between items-start mb-1.5">
              <span className="text-sm font-medium theme-text-primary">{file.name}</span>
              <button
                type="button"
                onClick={handleRemoveFile}
                className="text-red-400 hover:text-red-300 font-bold text-sm"
              >
                ✕
              </button>
            </div>
            {renderPreview()}
          </div>
        )}
      </div>
    );
  }
);

FileUploadField.displayName = 'FileUploadField';
